import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { Box, Button, Grid } from '@mui/material';
import { Field, Form } from 'react-final-form';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BasicLayout from '../../../common/layouts/basicLayout';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { fetchAllAsync as fetchAllFornecedores } from '../../../../features/fornecedor-favorecido/FornecedorSlice';
import { selectUnidadeSelecionada } from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import QCXFinalTextField from '../../../../shared-components/final-text-field/QCXFinalTextField';
import { MultiSelectFornecedor } from '../../financeiro/relatorioMovimentacao/MultiSelectFornecedor';
import MultiSelectClientes from '../planilhas/components/MultiSelectClientes';
import { generateSpreadsheetContabilSistemaUnico } from './contabilSistemaUnico.helpers';

const ContabilSistemaUnicoPage = () => {
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  const selectedUnit = useSelector(selectUnidadeSelecionada);

  const [datas, setDatas] = useState({ dataInicio: '', dataFim: '' });

  useUnidadeNegocioGuard();

  useEffect(() => {
    dispatch(fetchAllFornecedores());
  }, [dispatch]);

  const validarDatas = (d: { dataInicio: string; dataFim: string }): boolean => {
    if (!d.dataInicio || !d.dataFim) return false;
    return new Date(d.dataInicio) <= new Date(d.dataFim);
  };

  const handleChangeDataInicio = (isoDate: string) => {
    setDatas((prev) => ({ ...prev, dataInicio: isoDate.split('T')[0] }));
  };

  const handleChangeDataFim = (isoDate: string) => {
    setDatas((prev) => ({ ...prev, dataFim: isoDate.split('T')[0] }));
  };

  const handleSubmit = useCallback(
    async (values: any) => {
      keycloak.updateToken(300);

      if (!validarDatas(datas)) {
        dispatch(setErrorFeedback({ message: 'Preencha as datas de pagamento (início e fim) corretamente.' }));
        return;
      }

      if (!token || !selectedUnit) {
        dispatch(setErrorFeedback({ message: 'Selecione uma unidade de negócio.' }));
        return;
      }

      const fornecedoresIds: number[] = (values.fornecedores ?? []).map((item: any) => Number(item.id));
      const clientesIds: number[] = (values.clientes ?? []).map((item: any) => Number(item.id));
      const valor: string | null = values.valor ? String(values.valor).trim() : null;

      try {
        const error = await generateSpreadsheetContabilSistemaUnico(
          datas.dataInicio,
          datas.dataFim,
          token,
          selectedUnit,
          fornecedoresIds,
          clientesIds,
          valor
        );
        if (error != null) {
          dispatch(setErrorFeedback({ message: error }));
        } else {
          dispatch(setSuccessFeedback({ message: 'Planilha gerada com sucesso!' }));
        }
      } catch (err) {
        dispatch(setErrorFeedback({ message: 'Erro inesperado ao gerar o relatório.' }));
      }

      keycloak.updateToken(300);
    },
    [datas, token, selectedUnit, dispatch, keycloak]
  );

  return (
    <BasicLayout title={'Contábil - Sistema Único'} icon={<AssessmentIcon color={'secondary'} />}>
      <Box padding={3}>
        <Form onSubmit={() => {}}>
          {({ values }) => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MultiSelectFornecedor />
                </Grid>
                <Grid item xs={12}>
                  <MultiSelectClientes name="clientes" label="Cliente" />
                </Grid>
                <Grid item xs={12}>
                  <QCXFinalTextField id="valor" key="valor" name="valor" label="Valor" />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    name="dataInicio"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="data-inicio"
                        name={input.name}
                        label="Data de pagamento (início)"
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        required
                        onChange={(date: any) => {
                          handleChangeDataInicio(date.toISOString());
                          input.onChange(date);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    name="dataFim"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="data-fim"
                        name={input.name}
                        label="Data de pagamento (fim)"
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        required
                        onChange={(date: any) => {
                          handleChangeDataFim(date.toISOString());
                          input.onChange(date);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleSubmit(values)}
                    disabled={!selectedUnit || !datas.dataInicio || !datas.dataFim}
                  >
                    Gerar Relatório
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </Box>
    </BasicLayout>
  );
};

export default ContabilSistemaUnicoPage;
